// Messages inbox — /<slug>/messages — host only
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, ArrowLeft, Trash2, Check, MessageSquare } from "lucide-react";
import PageShell from "../components/PageShell";
import { Albums, Messages } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import SignInButton from "../components/SignInButton";

export default function MessagesInbox() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [album, setAlbum] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [a, list] = await Promise.all([Albums.get(slug), Messages.list(slug)]);
      setAlbum(a); setMsgs(list);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 403) setForbidden(true);
      else toast.error("Could not load messages");
    } finally { setBusy(false); }
  };

  useEffect(() => { if (!loading) load(); /* eslint-disable-next-line */ }, [slug, loading, user]);

  const onMarkRead = async (mid) => {
    try {
      await Messages.markRead(mid);
      setMsgs((prev) => prev.map((m) => m.id === mid ? { ...m, read: true } : m));
    } catch { toast.error("Could not mark read"); }
  };

  const onDelete = async (mid) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await Messages.remove(mid);
      setMsgs((prev) => prev.filter((m) => m.id !== mid));
      toast.success("Deleted");
    } catch { toast.error("Could not delete"); }
  };

  if (loading || busy) {
    return <PageShell><div className="flex justify-center mt-24"><div className="meye-spinner" /></div></PageShell>;
  }

  if (forbidden || !user) {
    return (
      <PageShell headerRight={<SignInButton />}>
        <div className="glass-card p-8 mt-10 text-center" data-testid="messages-forbidden">
          <Inbox size={32} style={{ margin: "0 auto", color: "#7F4BFF" }} />
          <div className="text-lg font-semibold mt-3">Host inbox</div>
          <div className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>
            {user
              ? "Only the host of this album can see messages."
              : "Sign in with Google as the host to see messages from your guests."}
          </div>
        </div>
      </PageShell>
    );
  }

  const unread = msgs.filter((m) => !m.read).length;

  return (
    <PageShell
      headerLeft={
        <button onClick={() => navigate(`/${slug}/gallery`)} className="meye-chip" style={{ padding: "8px 12px" }} data-testid="back-to-gallery-from-inbox">
          <ArrowLeft size={14} /> Back
        </button>
      }
      headerRight={<SignInButton />}
    >
      <div className="flex items-center gap-3 mt-1 mb-5">
        <div
          className="flex items-center justify-center"
          style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(127,75,255,0.18)", border: "1px solid rgba(127,75,255,0.5)" }}
        >
          <Inbox size={22} color="#7F4BFF" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xl font-semibold tracking-tight">Inbox</div>
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
            {album?.name} · {msgs.length} {msgs.length === 1 ? "message" : "messages"}{unread > 0 && ` · ${unread} unread`}
          </div>
        </div>
      </div>

      {msgs.length === 0 ? (
        <div className="glass-card p-8 text-center" style={{ color: "rgba(255,255,255,0.6)" }} data-testid="empty-inbox">
          <MessageSquare size={28} style={{ margin: "0 auto", opacity: 0.5 }} />
          <div className="mt-3">No messages yet.</div>
          <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.4)" }}>
            Guests can reach you via the "Message the Host" button in the gallery.
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5" data-testid="messages-list">
          {msgs.map((m) => (
            <div
              key={m.id}
              className="glass-card p-4"
              data-testid={`message-row-${m.id}`}
              style={!m.read ? { borderColor: "rgba(127,75,255,0.45)", background: "rgba(127,75,255,0.06)" } : {}}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="font-semibold flex items-center gap-2">
                  {m.sender_name}
                  {!m.read && (
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: "#7F4BFF", letterSpacing: 1 }}
                    >
                      • NEW
                    </span>
                  )}
                </div>
                <div className="text-xs" style={{ color: "rgba(255,255,255,0.45)" }}>
                  {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
              <div className="text-sm whitespace-pre-wrap" style={{ color: "rgba(255,255,255,0.85)" }}>
                {m.text}
              </div>
              <div className="flex gap-2 mt-3">
                {!m.read && (
                  <button
                    onClick={() => onMarkRead(m.id)}
                    className="meye-chip"
                    style={{ padding: "6px 10px", fontSize: 12 }}
                    data-testid={`mark-read-${m.id}`}
                  >
                    <Check size={12} /> Mark read
                  </button>
                )}
                <button
                  onClick={() => onDelete(m.id)}
                  className="meye-chip"
                  style={{ padding: "6px 10px", fontSize: 12, background: "rgba(255,107,107,0.08)", borderColor: "rgba(255,107,107,0.3)", color: "#FF6B6B" }}
                  data-testid={`delete-message-${m.id}`}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
}
